import { useQuery } from 'react-query';
import { useAuth0 } from '@auth0/auth0-react';
import setToken from '../../utils/api';
import { SummaryRange, rangeQuery } from '../../utils/summaryRange';

// Dashboard KPIs from GET api/orders/summary (OrderDetailsApi, Orders/Repository/Summary.cs).
// `now` is the backlog by status, with no date range; its groups match the sidebar badges.
// `received` and `delivered` are over `range`, which the API resolves in Toronto time and
// echoes back so the strip shows exactly the days it computed over. `prior` is the same-length
// period just before the range. Optional values are null when there is nothing to measure.
export interface OrderSummary {
  range: {
    preset: string;
    from: string;
    to: string;
    days: number;
    timeZone: string;
  };
  now: {
    newOrders: { count: number };
    awaitingKitting: { count: number; oldestDays: number | null };
    inShipping: { count: number; inProgress: number };
    inTransit: { count: number; avgDays: number | null };
  };
  received: { count: number; prior: number };
  delivered: {
    count: number;
    prior: number;
    onTimePercent: number | null;
    avgDaysToDeliver: number | null;
  };
}

export default function useOrderSummary(range: SummaryRange) {
  const { getAccessTokenSilently } = useAuth0();
  const query = rangeQuery(range);
  return useQuery<OrderSummary | undefined>(
    ['order-summary', query],
    async () => {
      const token = await getAccessTokenSilently();
      const api = await setToken(token);
      const response = await api.get<OrderSummary>(
        `api/orders/summary?${query}`,
        {},
      );
      if (response?.status !== 200) {
        throw new Error('No order summary');
      }
      return response.data;
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      keepPreviousData: true,
    },
  );
}
